import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/contexts/AuthContext";
import { Building2, MapPin, Users, Activity, TrendingUp } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const KPI = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="border border-[#E5E7EB] bg-white rounded-md p-6 hover:border-[#0B1F3A]/30 hover:shadow-sm transition-all" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
    <div className="flex items-start justify-between">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold">{label}</div>
      <div className={`w-8 h-8 rounded flex items-center justify-center ${accent ? "bg-[#FFD400]" : "bg-[#0B1F3A]"}`}>
        <Icon size={16} className={accent ? "text-[#0B1F3A]" : "text-[#FFD400]"} />
      </div>
    </div>
    <div className="mt-4 font-mono-data text-4xl font-semibold tracking-tighter text-[#0B1F3A]">{value}</div>
    {sub && <div className="mt-1 text-xs text-[#6B7280]">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/dashboard/metrics").then((r) => setMetrics(r.data)).catch((e) => setErr(e?.message));
  }, []);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <div className="inline-block text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-0.5 bg-[#FFD400] text-[#0B1F3A] rounded-sm">Fase 2 · Operação</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2 text-[#0B1F3A]">
          Olá, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">
          Perfil ativo: <span className="text-[#0A0A0A] font-semibold">{ROLE_LABELS[user?.role]}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Cidades" value={metrics?.cities?.active ?? "—"} sub={`${metrics?.cities?.total ?? 0} totais`} icon={MapPin} accent />
        <KPI label="Imobiliárias" value={metrics?.agencies?.active ?? "—"} sub={`${metrics?.agencies?.total ?? 0} totais`} icon={Building2} />
        <KPI label="Usuários" value={metrics?.users?.active ?? "—"} sub={`${metrics?.users?.total ?? 0} totais`} icon={Users} />
        <KPI label="Status" value="OK" sub="Sistema operacional" icon={Activity} />
      </div>

      {user?.role === "admin_geral" && metrics?.agencies_by_city?.length > 0 && (
        <div className="border border-[#E5E7EB] bg-white rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold">Distribuição</div>
              <div className="font-display text-lg font-bold mt-1 text-[#0B1F3A]">Imobiliárias por cidade</div>
            </div>
            <TrendingUp size={16} className="text-[#6B7280]" />
          </div>
          <div className="h-64" data-testid="chart-agencies-by-city">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={metrics.agencies_by_city}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="city" stroke="#6B7280" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6B7280" tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E5E7EB", fontSize: 12, color: "#0A0A0A" }} cursor={{ fill: "#F5F6F8" }} />
                <Bar dataKey="count" fill="#0B1F3A" radius={[3, 3, 0, 0]}>
                  {metrics.agencies_by_city.map((_, i) => <Cell key={i} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="border border-[#E5E7EB] bg-white rounded-md p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold mb-3">Próximos passos</div>
        <ul className="text-sm text-[#0A0A0A] space-y-2 font-mono-data">
          <li className="flex gap-2"><span className="text-[#FFD400]">→</span> Fase 2: solicitações + agenda por cidade + regra 130 m²</li>
          <li className="flex gap-2"><span className="text-[#FFD400]">→</span> Fase 3: Kanban operacional, laudos, supervisores e vistoriadores</li>
          <li className="flex gap-2"><span className="text-[#FFD400]">→</span> Fase 4: financeiro, fechamento mensal e integração Asaas</li>
          <li className="flex gap-2"><span className="text-[#FFD400]">→</span> Fase 5+: CRM, suporte, cursos, dashboards Power BI</li>
        </ul>
      </div>

      {err && <div className="text-red-600 text-sm">{err}</div>}
    </div>
  );
}
